"use client";

import { useRef, useState, ReactNode } from "react";
import { Card, Group, Text, Button, Tooltip, Box, Stack } from "@mantine/core";
import { IconCode, IconLink, IconDownload, IconCheck } from "@tabler/icons-react";
import { LAST_UPDATED } from "./data";

interface StatCardProps {
  id: string;
  title: string;
  subtitle?: string;
  source?: string;
  children: ReactNode;
  embedWidth?: number;
  embedHeight?: number;
}

const SITE_URL = "https://www.coloranalysistech.com";

export default function StatCard({
  id,
  title,
  subtitle,
  source,
  children,
  embedWidth = 640,
  embedHeight = 560,
}: StatCardProps) {
  // capture wraps title + subtitle + chart + source + brand — full standalone image
  const captureRef = useRef<HTMLDivElement>(null);
  const [copied, setCopied] = useState<"embed" | "link" | null>(null);

  const anchorUrl = `${SITE_URL}/color-analysis-statistics#${id}`;
  const embedUrl = `${SITE_URL}/embed/stats/${id}`;
  const embedCode = `<iframe src="${embedUrl}" width="${embedWidth}" height="${embedHeight}" frameborder="0" style="border:0;max-width:100%" title="${title} — ColorAnalysisTech"></iframe>\n<p><a href="${anchorUrl}">${title}</a> via <a href="${SITE_URL}">ColorAnalysisTech</a></p>`;

  const copy = async (text: string, kind: "embed" | "link") => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(kind);
      setTimeout(() => setCopied(null), 1800);
    } catch {
      // ignore
    }
  };

  const downloadPng = async () => {
    if (!captureRef.current) return;
    const mod = await import("html-to-image");
    const dataUrl = await mod.toPng(captureRef.current, {
      backgroundColor: "#ffffff",
      pixelRatio: 2,
      cacheBust: true,
    });
    const link = document.createElement("a");
    link.download = `coloranalysistech-${id}.png`;
    link.href = dataUrl;
    link.click();
  };

  return (
    <Card
      withBorder
      radius="lg"
      p={0}
      id={id}
      style={{
        scrollMarginTop: 80,
        overflow: "hidden",
        boxShadow: "0 1px 2px rgba(0,0,0,0.04)",
      }}
    >
      {/* CAPTURED — everything inside ends up in the PNG */}
      <Box ref={captureRef} p="xl" bg="white">
        <Stack gap={4} mb="lg">
          <Group gap={6} mb={2}>
            <Text fz={10} fw={600} tt="uppercase" style={{ letterSpacing: "0.2em", color: "#A0522D" }}>
              ColorAnalysisTech
            </Text>
            <Text fz={10} c="dimmed" style={{ letterSpacing: "0.12em" }}>
              · {LAST_UPDATED}
            </Text>
          </Group>
          <Text
            fz={22}
            fw={500}
            style={{ letterSpacing: "-0.015em", lineHeight: 1.25, color: "#1A1A2E" }}
          >
            {title}
          </Text>
          {subtitle && (
            <Text fz={14} c="dimmed" style={{ lineHeight: 1.5, maxWidth: 720 }}>
              {subtitle}
            </Text>
          )}
        </Stack>

        <Box>{children}</Box>

        <Box
          mt="lg"
          pt="md"
          style={{
            borderTop: "1px solid #f0ece6",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 12,
            flexWrap: "wrap",
          }}
        >
          <Text fz={11} c="dimmed" style={{ fontStyle: "italic" }}>
            {source || "ColorAnalysisTech aggregate data"}
          </Text>
          <Text fz={11} style={{ color: "#A0522D", fontWeight: 600, letterSpacing: "0.05em" }}>
            coloranalysistech.com/color-analysis-statistics
          </Text>
        </Box>
      </Box>

      {/* CONTROLS — outside the captured region */}
      <Box
        p="sm"
        px="xl"
        style={{
          borderTop: "1px solid var(--mantine-color-gray-2)",
          background: "#fafaf7",
        }}
      >
        <Group justify="flex-end" gap="xs">
          <Tooltip label={copied === "embed" ? "Copied!" : "Copy embed code"}>
            <Button
              size="xs"
              variant="default"
              leftSection={copied === "embed" ? <IconCheck size={14} /> : <IconCode size={14} />}
              onClick={() => copy(embedCode, "embed")}
            >
              Embed
            </Button>
          </Tooltip>
          <Tooltip label={copied === "link" ? "Copied!" : "Copy direct link"}>
            <Button
              size="xs"
              variant="default"
              leftSection={copied === "link" ? <IconCheck size={14} /> : <IconLink size={14} />}
              onClick={() => copy(anchorUrl, "link")}
            >
              Link
            </Button>
          </Tooltip>
          <Tooltip label="Download as PNG (full context baked in)">
            <Button
              size="xs"
              variant="default"
              leftSection={<IconDownload size={14} />}
              onClick={downloadPng}
            >
              PNG
            </Button>
          </Tooltip>
        </Group>
      </Box>
    </Card>
  );
}
